import React, { useContext } from "react";
import { Formik, Form, Field, getIn, ErrorMessage } from "formik";
import * as Yup from "yup";

import { merge as mergeObjects } from "lodash";

import DispatchContext from "../../contexts/DispatchContext";
import DataFetcher from "../../utils/DataFetcher";
import FileUpload from "../elements/FileUpload";
import FieldSwitch from "../elements/FieldSwitch";
import ErrorLine from "../elements/ErrorLine";

import formdata from "../../data/formdata";
import { getResponse, dispError, dispSuccess } from "../../utils/Tools";

/**
 * Mandanten Formular
 * @param {*} props
 */
function FormClient(props) {
  const appDispatch = useContext(DispatchContext);

  const validation = {
    name: Yup.string().required("Bitte geben Sie den Namen des Mandanten ein."),
    subdomain: Yup.string()
      .matches(/[a-z0-9-]+/, "Subdomain darf nur Buchstaben, Zahlen und - enthalten.")
      .required("Bitte geben Sie die Subdomain des Mandanten ein."),
    address: Yup.object().shape({
      street: Yup.string().required("Bitte geben Sie Straße und Hausnummer ein."),
      zipCode: Yup.number().required("Bitte geben Sie eine PLZ ein."),
      city: Yup.string().required("Bitte geben Sie einen Ort ein."),
      region: Yup.string().required("Bitte wählen Sie ein Bundesland aus.")
    }),
    contact: Yup.object().shape({
      salutation: Yup.string().required("Bitte wählen Sie eine Anrede aus."),
      firstname: Yup.string().required("Bitte geben Sie einen Vornamen ein."),
      lastname: Yup.string().required("Bitte geben Sie einen Nachnamen ein."),
      email: Yup.string().email("Keine gültige E-Mail-Adresse").required("Bitte geben Sie eine E-Mail-Adresse ein.")
    }),
    type: Yup.string().required("Bitte wählen Sie die Art des Mandanten aus.")
  };

  if (props.modus !== "edit") {
    validation.privacy_contact = Yup.object().shape({
      email: Yup.string().email("Keine gültige E-Mail-Adresse").required("Bitte geben Sie eine E-Mail-Adresse ein.")
    });
  }

  // NOTE: Make privacy contact email required

  const client_types = formdata.client_types.filter(element => ["verteilender-mandant", "lizensierender-mandant", "testender-mandant", "autolizensierender-mandant"].includes(element[0]));

  const defaultValues = {
    name: "",
    subdomain: "",
    logo: "",
    address: {
      co: "",
      street: "",
      addition: "",
      zipCode: "",
      city: "",
      region: ""
    },
    contact: {
      salutation: "",
      firstname: "",
      lastname: "",
      position: "",
      email: "",
      tel: ""
    },
    capabilities: {
      client: { seeTestResults: false },
      institution: { seeTestResults: false },
      participant: { seeTestResults: false }
    },
    resultPdfProvider: "default",
    type: ""
  };

  if (props.modus !== "edit") {
    defaultValues.privacy_contact = {
      firstname: "",
      lastname: "",
      email: ""
    };
  }

  let initialValues = { ...defaultValues };

  // Edit client
  if (props.prefill) mergeObjects(initialValues, props.prefill);
  const targetURL = props.modus && props.modus === "edit" ? "UPDATE" : "CREATE";

  const onSubmit = (values, { resetForm, setSubmitting }) => {
    let payload = { ...values };
    if (props.modus === "edit") payload._id = props.prefill._id;
    DataFetcher.fetch(DataFetcher.CLIENT[targetURL], payload)
      .then(response => {
        if (!response.success) {
          setSubmitting(false);
          return appDispatch(dispError(response.msg));
        }
        let msg = props.modus === "edit" ? getResponse("002") : getResponse("001");
        appDispatch(dispSuccess(msg));
        if (props.modus === "edit") {
          props.onUpdate();
        } else {
          resetForm({ values: "" });
          appDispatch({ type: "client-created" });
        }
      })
      .catch(error => {
        console.error(error);
        setSubmitting(false);
      });
  };

  return (
    <Formik initialValues={initialValues} validationSchema={Yup.object().shape(validation)} onSubmit={onSubmit}>
      {({ errors, touched, isSubmitting }) => (
        <Form autoComplete="on">
          <div className="field">
            <div className="control">
              <div className={`select${touched.type && errors.type ? " is-danger" : ""}`}>
                <Field as="select" name="type">
                  <option>Art des Mandanten*</option>
                  {client_types.map((item, index) => {
                    return (
                      <option key={`usertype-${index}`} value={item[0]}>
                        {item[1]}
                      </option>
                    );
                  })}
                </Field>
              </div>
            </div>
            <ErrorMessage component={ErrorLine} name="type" />
          </div>
          <div className="field">
            <label className="label">Name*</label>
            <div className="control">
              <Field name="name" type="text" className={`input${touched.name && errors.name ? " is-danger" : ""}`} />
            </div>
            <ErrorMessage component={ErrorLine} name="name" />
          </div>
          <div className="field">
            <label className="label">Subdomain* (z.B. beispiel-test)</label>
            <div className="control">
              <Field name="subdomain" type="text" className={`input${touched.name && errors.name ? " is-danger" : ""}`} />
            </div>
            <ErrorMessage component={ErrorLine} name="subdomain" />
          </div>
          <div className="field">
            <label className="label">Logo (jpg oder png, max. 50 KB)</label>
            <div className="control">
              <Field name="logo" component={FileUpload} />
            </div>
          </div>
          <hr />
          <h2 className="title is-4">Adresse</h2>
          <div className="field">
            <label className="label">c/o</label>
            <div className="control">
              <Field name="address.co" type="text" className={`input`} />
            </div>
          </div>
          <div className="field">
            <label className="label">Straße und Hausnummer*</label>
            <div className="control">
              <Field name="address.street" type="text" className={`input`} />
            </div>
            <ErrorMessage component={ErrorLine} name="address.street" />
          </div>
          <div className="field">
            <label className="label">Adresszusatz</label>
            <div className="control">
              <Field name="address.addition" type="text" className={`input`} />
            </div>
          </div>
          <div className="field field-body">
            <div className="field">
              <label className="label">PLZ*</label>
              <div className="control">
                <Field name="address.zipCode" type="number" className={`input${getIn(touched, "address.zipCode") && getIn(errors, "address.zipCode") ? " is-danger" : ""}`} />
              </div>
              <ErrorMessage component={ErrorLine} name="address.zipCode" />
            </div>
            <div className="field">
              <label className="label">Ort*</label>
              <div className="control">
                <Field name="address.city" type="text" className={`input${getIn(touched, "address.city") && getIn(errors, "address.city") ? " is-danger" : ""}`} />
              </div>
              <ErrorMessage component={ErrorLine} name="address.city" />
            </div>
          </div>
          <div className="field">
            <label className="label">Bundesland*</label>
            <div className="control">
              <div className={`select${getIn(touched, "address.region") && getIn(errors, "address.region") ? " is-danger" : ""}`}>
                <Field as="select" name="address.region">
                  <option>Wählen</option>
                  {formdata.federal_states.map((item, index) => {
                    return (
                      <option key={`state-${index}`} value={item[0]}>
                        {item[1]}
                      </option>
                    );
                  })}
                </Field>
              </div>
            </div>
            <ErrorMessage component={ErrorLine} name="address.region" />
          </div>
          <hr />
          <h2 className="title is-4">Kontakt</h2>
          <div className="field">
            <label className="label">Anrede*</label>
            <div className="control">
              <div className={`select${getIn(touched, "contact.salutation") && getIn(errors, "contact.salutation") ? " is-danger" : ""}`}>
                <Field as="select" name="contact.salutation">
                  <option>Wählen</option>
                  {formdata.salutation.map((item, index) => {
                    return (
                      <option key={`state-${index}`} value={item[0]}>
                        {item[1]}
                      </option>
                    );
                  })}
                </Field>
              </div>
            </div>
            <ErrorMessage component={ErrorLine} name="contact.salutation" />
          </div>
          <div className="field field-body">
            <div className="field">
              <label className="label">Vorname*</label>
              <div className="control">
                <Field name="contact.firstname" type="text" className={`input${getIn(touched, "contact.firstname") && getIn(errors, "contact.firstname") ? " is-danger" : ""}`} />
              </div>
              <ErrorMessage component={ErrorLine} name="contact.firstname" />
            </div>
            <div className="field">
              <label className="label">Nachname*</label>
              <div className="control">
                <Field name="contact.lastname" type="text" className={`input${getIn(touched, "contact.lastname") && getIn(errors, "contact.lastname") ? " is-danger" : ""}`} />
              </div>
              <ErrorMessage component={ErrorLine} name="contact.lastname" />
            </div>
          </div>
          <div className="field">
            <label className="label">Funktion</label>
            <div className="control">
              <Field name="contact.position" type="text" className={`input`} />
            </div>
          </div>
          <div className="field">
            <label className="label">E-Mail-Adresse*</label>
            <p className="control has-icons-left">
              <Field name="contact.email" type="email" className={`input${getIn(touched, "contact.email") && getIn(errors, "contact.email") ? " is-danger" : ""}`} />
              <span className="icon is-small is-left">
                <i className="fas fa-envelope"></i>
              </span>
            </p>
            <ErrorMessage component={ErrorLine} name="contact.email" />
          </div>
          <div className="field">
            <label className="label">Telefonnummer</label>
            <div className="control">
              <Field name="contact.tel" type="tel" className={`input`} />
            </div>
          </div>
          <hr />
          {props.modus !== "edit" && (
            <>
              <h2 className="title is-4">Datenschutzbeauftragter</h2>
              <div className="field field-body">
                <div className="field">
                  <label className="label">Vorname</label>
                  <div className="control">
                    <Field name="privacy_contact.firstname" type="text" className={`input`} />
                  </div>
                </div>
                <div className="field">
                  <label className="label">Nachname</label>
                  <div className="control">
                    <Field name="privacy_contact.lastname" type="text" className={`input`} />
                  </div>
                </div>
              </div>
              <div className="field">
                <label className="label">E-Mail-Adresse*</label>
                <p className="control has-icons-left">
                  <Field name="privacy_contact.email" type="email" className={`input${getIn(touched, "privacy_contact.email") && getIn(errors, "privacy_contact.email") ? " is-danger" : ""}`} />
                  <span className="icon is-small is-left">
                    <i className="fas fa-envelope"></i>
                  </span>
                </p>
                <ErrorMessage component={ErrorLine} name="privacy_contact.email" />
              </div>
              <hr />
            </>
          )}
          <h2 className="title is-4">Systemrelevant</h2>
          <div className="field">
            <div className="control field-body">
              <FieldSwitch className="switch is-rounded is-info" name="isNotReporting">
                Daten fließen nicht in die Statistik ein
              </FieldSwitch>
            </div>
          </div>
          <h2 className="title is-4">Einsicht Testergebnis</h2>
          <div className="field">
            <div className="control field-body">
              <FieldSwitch className="switch is-rounded is-info" name="capabilities.client.seeTestResults" disabled>
                Mandant
              </FieldSwitch>
              <FieldSwitch className="switch is-rounded is-info" name="capabilities.institution.seeTestResults">
                Institution
              </FieldSwitch>
              <FieldSwitch className="switch is-rounded is-info" name="capabilities.participant.seeTestResults">
                Testteilnehmer
              </FieldSwitch>
            </div>
          </div>
          <hr />
          <div className="field">
            <label className="label">PDF-Provider</label>
            <div className="control">
              <div className={`select`}>
                <Field as="select" name="resultPdfProvider">
                  {formdata.pdfProviders.map((item, index) => {
                    return (
                      <option key={`pdfprov-${index}`} value={item[0]}>
                        {item[1]}
                      </option>
                    );
                  })}
                </Field>
              </div>
            </div>
          </div>
          <hr />
          <div className="field">
            <div className="control field-body">
              <button className="button is-success" type="submit" disabled={isSubmitting}>
                Speichern
              </button>
              <button className="button ml-2" type="button" onClick={props.onCancel}>
                Abbrechen
              </button>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
}
export default FormClient;
